import {
  Component,
  Element,
  Event,
  EventEmitter,
  Fragment,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch
} from "@stencil/core";
import { KeyIds } from "../../constants/keys";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { ListLocalization, ListSchemeDefinition, ListSchemeName } from "./constants/list-types";
import { isMobile } from "../../utils/screen-helper";
import pfx from "../../utils/style-class-prefix-helper";
import { LIST_LOCALIZATION } from "./constants/list-i18n";
import { detectSlotChanges } from "../../utils/slot-helper";
import { getNestedChildrenOf } from "../../utils/dom-helper";
import { scrollToElement } from "../../utils/scroll-helper";
import webComponentsDebug from "../../utils/log-helper";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { ListSchemeConfiguration } from "../../constants/colors";
import { LIST_SCHEME_CONFIG, LIST_SCHEME_MAP } from "./constants/list-constants";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot preloader - place desired preloader component here.
 */
@Component({
  tag: "eon-ui-list",
  styleUrl: "list.scss",
  shadow: true
})
export class List
  implements LocalizableComponent<ListLocalization>, ColorableComponent<ListSchemeConfiguration<ListSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ListSchemeConfiguration<ListSchemeDefinition> = LIST_SCHEME_CONFIG;
  schemeMap = LIST_SCHEME_MAP;

  @Element() hostElement: HTMLEonUiListElement;
  @State() isMobile: boolean;
  listItems: Element[] = [];
  @State() activeScheme: ListSchemeDefinition;
  @State() visibleListItems: Element[] = [];
  @State() begin: number;
  @State() end: number;
  @State() loading: boolean = false;
  @State() scrolling: boolean = false;
  @State() blockScrolling: boolean = true;
  @State() hidePagination: boolean = false;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ListSchemeName;

  /**
   * Fires when the page was increased
   */
  @Event() pageIncreased: EventEmitter<number>;

  /**
   * Fires when the page was decreased
   */
  @Event() pageDecreased: EventEmitter<number>;

  /**
   * Fires when the page was changed
   */
  @Event() pageChanged: EventEmitter<number>;

  /**
   * Fires when the page Size was changed
   */
  @Event() pageSizeChanged: EventEmitter<number>;

  pageNumberInput: HTMLEonUiInputElement;
  listElement: HTMLElement;

  localization: Localization<ListLocalization> = LIST_LOCALIZATION;
  currentLocalization: ListLocalization;

  /**
   * The Items to be viewed per page
   */
  @State() pageSize: number = 5;

  /**
   * Comma seperated list of page sizes
   */
  @Prop() pageSizes: string = "5,10,20,50,100";

  /**
   * Initial page size
   */
  @Prop({ mutable: true }) initialPageSize: number;

  /**
   * represents the current page index
   */
  @Prop({ mutable: true, reflect: true }) currentPage: number = 1;

  /**
   * Provide to total number of items. Use this if you want to use this component with a backend api with paging.
   */
  @Prop() totalItems: number;

  /**
   * Disables client side paging. Use this if your paging is handled in the backend. This property disables the client side paging and always shows all child items
   */
  @Prop() serverMode: boolean = false;

  /**
   * Select type of pagination
   */
  @Prop() paginationType: "dropdown" | "numbers" = "dropdown";

  private get sortedPageSizeArray(): number[] {
    return this.pageSizes
      .split(",")
      .map((num) => parseInt(num))
      .sort((a, b) => {
        return a - b;
      });
  }

  private get numberOfPages(): number {
    const totalItems = this.totalItems ?? this.listItems.length;
    return Math.ceil(Number(totalItems) / this.pageSize);
  }

  private get listLength(): number {
    return this.totalItems ?? this.listItems.length;
  }

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.isMobile = isMobile();
  }

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
    this.storeItemsInArray();
    this.verifyInitialPageSize();
    this.pageSize = this.initialPageSize;
    this.getVisibleItems();
    this.onViewportSizeChange();
    this.hidePaginationWrapper();
    detectSlotChanges(() => {
      this.storeItemsInArray();
      this.getVisibleItems();
      this.hidePaginationWrapper();
    }, this.hostElement);
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  storeItemsInArray() {
    this.listItems = Array.from(
      getNestedChildrenOf(this.hostElement, "eon-ui-list-item") as HTMLCollectionOf<HTMLEonUiListItemElement>
    );
  }

  @Watch("initialPageSize")
  changePageSize() {
    this.pageSize = this.initialPageSize;
  }

  @Watch("currentPage")
  @Watch("pageSize")
  getVisibleItems() {
    if (webComponentsDebug) {
      console.log(
        "Watch currentPage && pageSize ==> ",
        "initialPageSize:",
        this.initialPageSize,
        "pageSize:",
        this.pageSize,
        "currentPage:",
        this.currentPage
      );
    }
    this.begin = (this.currentPage - 1) * this.pageSize;
    this.end = this.begin + this.pageSize > this.listLength ? this.listLength : this.begin + this.pageSize;

    if (!this.serverMode) {
      const showNextPage = () => {
        this.listItems.forEach((listItem) => {
          listItem.removeAttribute("visible");
        });

        this.visibleListItems = this.listItems.slice(this.begin, this.end);
        this.visibleListItems.forEach((visibleListItem) => {
          visibleListItem.setAttribute("visible", "true");
        });
      };

      showNextPage();
    } else {
      this.visibleListItems = this.listItems;
      this.visibleListItems.forEach((visibleListItem) => {
        visibleListItem.setAttribute("visible", "true");
      });
    }
  }

  /**
   * Method to reinitiate/reload the page, e.g. listElement.reInitiateList();
   */
  @Method()
  async reInitiateList(): Promise<void> {
    this.currentPage = 1;
  }

  private verifyInitialPageSize() {
    if (!this.initialPageSize || !this.pageSizes.includes(this.initialPageSize.toString())) {
      this.initialPageSize = this.sortedPageSizeArray[0];
    }
  }

  private async goToTop() {
    this.scrolling = true;
    await scrollToElement(this.hostElement, {
      scrollContainer: window,
      offset: -200
    });
    this.scrolling = false;
  }

  private setPageSize(pageSize: number) {
    if (pageSize) {
      this.currentPage = 1; //reset current page
      this.pageSize = pageSize;
      this.pageSizeChanged.emit(pageSize);
      if (!this.blockScrolling) {
        this.goToTop();
      }
      this.blockScrolling = false;
    }
  }

  private increasePage() {
    if (this.currentPage != this.numberOfPages) {
      this.currentPage++;
      this.pageIncreased.emit(this.currentPage);
      this.goToTop();
    }
  }

  private decreasePage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.pageDecreased.emit(this.currentPage);
      this.goToTop();
    }
  }

  private setCurrentPage(page: number) {
    if (page > this.numberOfPages) {
      this.currentPage = this.numberOfPages;
    } else {
      this.currentPage = page;
    }
    if (page < 1) {
      this.currentPage = 1;
    }
    this.pageChanged.emit(this.currentPage);
    this.goToTop();
  }

  private get deactivatePageInput() {
    return !this.listLength;
  }

  private get deactivateNextButton() {
    return this.currentPage === this.numberOfPages;
  }

  private get deactivatePreviousButton() {
    return this.currentPage === 1;
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.ENTER:
        const pageNumber =
          this.paginationType === "dropdown"
            ? Number(this.pageNumberInput.value)
            : parseInt((e.currentTarget as HTMLElement).innerHTML);
        this.setCurrentPage(pageNumber);
        e.preventDefault();
        break;
    }
  }

  createPagination = () => {
    const paginationSize = isMobile() ? 5 : 7;
    const paginationMargin = isMobile() ? 2 : 3;

    const paginationElements = [];
    // List all the pages if number of pages smaller than pagination size
    if (this.numberOfPages <= paginationSize) {
      for (let i = 1; i <= this.numberOfPages; i++) {
        paginationElements.push(i);
      }
    } else if (this.currentPage <= paginationSize - paginationMargin) {
      // ... is only on the second-to-last place
      for (let i = 1; i <= paginationSize - 2; i++) {
        paginationElements.push(i);
      }
      paginationElements.push("...");
      paginationElements.push(this.numberOfPages);
    } else if (this.currentPage > this.numberOfPages - paginationSize + paginationMargin) {
      // ... is only on the second place
      paginationElements.push(1);
      paginationElements.push("...");
      for (let i = this.numberOfPages - paginationSize + 3; i <= this.numberOfPages; i++) {
        paginationElements.push(i);
      }
    } else {
      // ... is on the second and second-to-last place
      paginationElements.push(1);
      paginationElements.push("...");
      if (paginationSize === 7) {
        paginationElements.push(this.currentPage - 1);
      }
      paginationElements.push(this.currentPage);
      if (paginationSize === 7) {
        paginationElements.push(this.currentPage + 1);
      }
      paginationElements.push("...");
      paginationElements.push(this.numberOfPages);
    }

    return (
      <Fragment>
        <div class={pfx("pagination")}>
          {paginationElements.map((elm) => {
            return (
              <div
                class={pfx({
                  "pagination-element": true,
                  active: this.currentPage === elm,
                  disabled: elm === "..."
                })}
                onClick={() => this.setCurrentPage(elm)}
                onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e)}
                tabIndex={0}
                role="link"
              >
                {elm}
              </div>
            );
          })}
        </div>
      </Fragment>
    );
  };

  @Watch("totalItems")
  @Watch("pageSizes")
  hidePaginationWrapper() {
    if (this.listLength <= this.sortedPageSizeArray[0]) {
      this.hidePagination = true;
    } else {
      this.hidePagination = false;
    }
  }

  render() {
    return (
      <Host>
        <div class={pfx({ "list-wrapper": true, loading: this.loading || this.scrolling })}>
          <div ref={(element) => (this.listElement = element)} class={pfx({ list: true })}>
            <slot />
          </div>
          {!this.hidePagination && (
            <div class={pfx({ paging: true, [`pagination-type-${this.paginationType}`]: this.paginationType })}>
              <div class={pfx({ paging__column: true })}>
                <eon-ui-text text-style="copy" class={pfx({ paging__items__label: true })}>
                  {this.currentLocalization.itemsPerPage}:
                </eon-ui-text>
                <eon-ui-dropdown
                  size="small"
                  name="itemsPerPage"
                  onValueChanged={(event) => this.setPageSize(Number(event.detail))}
                  class={pfx({ paging__items__control: true })}
                  selected={`${this.initialPageSize}`}
                >
                  {this.sortedPageSizeArray.map((pageSize) => {
                    if (Number(pageSize) <= this.listLength) {
                      return (
                        <eon-ui-dropdown-option value={`${pageSize}`} label={`${pageSize}`}></eon-ui-dropdown-option>
                      );
                    }
                  })}
                </eon-ui-dropdown>
              </div>
              <div
                class={pfx({
                  paging__column: true,
                  "paging__column--pagination-text": true
                })}
              >
                <eon-ui-text
                  text-style={this.isMobile ? "copy-small" : "copy"}
                  class={pfx({ paging__description: true })}
                >
                  {this.begin === 0 ? "1" : this.begin + 1} - {this.end} {this.currentLocalization.of} {this.listLength}{" "}
                  {this.currentLocalization.items}
                </eon-ui-text>
              </div>
              {this.paginationType === "dropdown" && (
                <div
                  class={pfx({
                    paging__column: true,
                    "paging__column--mobile-top": true,
                    "paging__column--mobile-100": true
                  })}
                >
                  <eon-ui-button
                    size="small"
                    icon="chevron_small_left"
                    class={pfx({ paging__current__button: true })}
                    onClick={() => this.decreasePage()}
                    disabled={this.deactivatePreviousButton}
                  ></eon-ui-button>
                  <eon-ui-input
                    ref={(el) => (this.pageNumberInput = el)}
                    size="small"
                    class={pfx({ paging__current__input: true })}
                    name="itemsVisible"
                    type="number"
                    onKeyDown={(e: KeyboardEvent) => this.handleKeyDown(e)}
                    value={this.currentPage.toString()}
                    disabled={this.deactivatePageInput}
                  ></eon-ui-input>
                  <eon-ui-text text-style="copy" id="" class={pfx({ paging__current__label: true })}>
                    {this.currentLocalization.of} {this.numberOfPages}
                  </eon-ui-text>
                  <eon-ui-button
                    size="small"
                    icon="chevron_small_right"
                    class={pfx({ paging__current__button: true })}
                    onClick={() => this.increasePage()}
                    disabled={this.deactivateNextButton}
                  ></eon-ui-button>
                </div>
              )}
              {this.paginationType === "numbers" && (
                <div
                  class={pfx({
                    paging__column: true,
                    "paging__column--pagination": true
                  })}
                >
                  <eon-ui-link
                    icon="chevron_small_left"
                    class={pfx({
                      "paging__current-button": true,
                      "pagination-element": true,
                      disabled: this.deactivatePreviousButton
                    })}
                    onClick={() => this.decreasePage()}
                    disabled={this.deactivatePreviousButton}
                  ></eon-ui-link>
                  {this.createPagination()}
                  <eon-ui-link
                    icon="chevron_small_right"
                    class={pfx({
                      "paging__current-button": true,
                      "pagination-element": true,
                      disabled: this.deactivateNextButton
                    })}
                    onClick={() => this.increasePage()}
                    disabled={this.deactivateNextButton}
                  ></eon-ui-link>
                </div>
              )}
            </div>
          )}
          <div class={pfx({ "preloading-overlay": true })}>
            <slot name="preloader" />
          </div>
        </div>
      </Host>
    );
  }
}
